// Connection-lost screen: ConnectionLostOverlay covers the whole app while the
// machine-state stream is down and clears itself once it reconnects;
// ConnectionLostPage shows the same card full-page while a stored session
// cannot be checked at start-up. Identical in development and production.
import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Fade,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { tokens } from "../theme";
import { useMachineState } from "../hooks/useMachineState";

// Hides the watchdog's immediate reconnect after a silent stall. A drop that
// fires onerror stays down for the provider's fixed 5 s retry, so it shows the
// overlay for about 2 s before "Connection restored".
const GRACE_MS = 3000;
// How long "Connection restored" stays up before the app is uncovered.
const RESTORED_MS = 1200;
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  70%, 100% { transform: scale(1.6); opacity: 0; }
`;

/**
 * The connection card's colours: one light and one dark value per entry.
 * Adjust them here; they start from the theme's alert colours and nothing
 * else in the app reads them.
 * @param {boolean} isDark - Dark mode.
 * @param {Object} colors - tokens(mode) from ../theme.
 * @returns {Object} Colours by part of the card.
 */
const cardPalette = (isDark, colors) => ({
  // "Connection lost": the cloud icon, its circle and the pulsing ring
  lostIcon: isDark ? colors.orangeAccent[500] : colors.orangeAccent[500],
  lostCircle: isDark ? "rgba(230, 77, 31, 0.12)" : "rgba(230, 77, 31, 0.10)",
  lostCircleBorder: isDark ? "rgba(230, 77, 31, 0.35)" : "rgba(230, 77, 31, 0.25)",
  // "Reconnecting automatically" pill and its spinner
  pillBackground: isDark ? "rgba(38, 198, 218, 0.08)" : "rgba(12, 93, 103, 0.06)",
  pillBorder: isDark ? "rgba(38, 198, 218, 0.30)" : "rgba(12, 93, 103, 0.20)",
  pillText: isDark ? colors.primary[800] : colors.primary[800],
  spinner: isDark ? colors.tealAccent[500] : colors.tealAccent[500],
  // Icons before "No need to refresh…" and "Still offline…"
  hintIcon: isDark ? colors.tealAccent[500] : colors.tealAccent[500],
  // "Connection restored": the check icon, its circle and the "Back online" pill
  restoredIcon: isDark ? "#26c6da" : colors.tealAccent[500],
  restoredCircle: isDark ? "rgba(38, 198, 218, 0.12)" : "rgba(12, 93, 103, 0.10)",
  restoredCircleBorder: isDark ? "rgba(38, 198, 218, 0.35)" : "rgba(12, 93, 103, 0.25)",
  restoredText: isDark ? "#cedfe1" : colors.tealAccent[700],
});

const pad = (n) => String(n).padStart(2, "0");

/**
 * Formats a duration as m:ss, or h:mm:ss from one hour on.
 * @param {number} seconds - Whole seconds.
 * @returns {string} The formatted duration.
 */
const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(seconds % 60)}` : `${m}:${pad(seconds % 60)}`;
};

const formatClock = (ts) =>
  new Date(ts).toLocaleTimeString([], {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });

/**
 * Whole seconds elapsed since a moment, re-rendering every second while running.
 * @param {number|null} since - Epoch ms to count from.
 * @param {boolean} running - False freezes the value (e.g. once restored).
 * @returns {number} Seconds since `since`, 0 when unset.
 */
const useSecondsSince = (since, running) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);
  return since ? Math.max(0, Math.floor((now - since) / 1000)) : 0;
};

const Hint = ({ icon: Icon, iconColor, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
    <Icon sx={{ fontSize: 20, mt: "1px", flexShrink: 0, color: iconColor }} />
    <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: "inherit" }}>
      {children}
    </Typography>
  </Box>
);

/**
 * The card shared by the overlay and the start-up page.
 * @param {Object} props
 * @param {number|null} props.since - Epoch ms when contact was lost.
 * @param {boolean} [props.restored=false] - Show the "Connection restored" state.
 * @param {boolean} [props.startup=false] - Wording for a session still being checked at start-up.
 */
const ConnectionLostCard = ({ since, restored = false, startup = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const colors = tokens(theme.palette.mode);
  const palette = cardPalette(isDark, colors);
  const reduceMotion = useMediaQuery(REDUCED_MOTION);
  const seconds = useSecondsSince(since, !restored);

  const badge = restored
    ? { icon: palette.restoredIcon, bg: palette.restoredCircle, border: palette.restoredCircleBorder }
    : { icon: palette.lostIcon, bg: palette.lostCircle, border: palette.lostCircleBorder };
  const pill = restored
    ? { icon: palette.restoredIcon, text: palette.restoredText,
        bg: palette.restoredCircle, border: palette.restoredCircleBorder }
    : { icon: palette.spinner, text: palette.pillText,
        bg: palette.pillBackground, border: palette.pillBorder };
  const titleColor = isDark ? colors.grey[900] : "#333";
  const leadColor = isDark ? colors.grey[800] : colors.grey[700];
  const bodyColor = isDark ? colors.grey[700] : colors.grey[600];

  let lead = "This screen can't reach the BatchMind server right now.";
  if (restored) lead = "The server is reachable again. Picking up where you left off.";
  else if (startup) lead = "BatchMind can't reach its server right now.";

  let pillIcon = <CheckRoundedIcon sx={{ fontSize: 18, color: pill.icon }} />;
  if (!restored) {
    pillIcon = reduceMotion
      ? <SyncRoundedIcon sx={{ fontSize: 18, color: pill.icon }} />
      : <CircularProgress size={15} thickness={5} disableShrink sx={{ color: pill.icon }} />;
  }
  let pillLabel = startup ? "Retrying automatically" : "Reconnecting automatically";
  if (restored) pillLabel = "Back online";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 440,
        px: { xs: 3, sm: 4.5 },
        pt: 4.5,
        pb: 3.5,
        borderRadius: "16px",
        border: `1px solid ${isDark ? colors.primary[300] : colors.grey[300]}`,
        backgroundColor: isDark ? colors.primary[200] : "#fff",
        boxShadow: isDark
          ? "0 16px 48px rgba(0, 0, 0, 0.55)"
          : "0 16px 48px rgba(0, 0, 0, 0.14)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box sx={{ position: "relative", width: 72, height: 72, mb: 2.5 }}>
        {!restored && !reduceMotion && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `2px solid ${badge.icon}`,
              animation: `${pulse} 2.4s ease-out infinite`,
            }}
          />
        )}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: badge.icon,
            backgroundColor: badge.bg,
            border: `1px solid ${badge.border}`,
            transition: "background-color 250ms, color 250ms, border-color 250ms",
          }}
        >
          {restored
            ? <CheckRoundedIcon sx={{ fontSize: 38 }} />
            : <CloudOffRoundedIcon sx={{ fontSize: 36 }} />}
        </Box>
      </Box>

      <Typography
        id="connection-lost-title"
        component="h2"
        sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25, color: titleColor, mb: 1 }}
      >
        {restored ? "Connection restored" : "Connection lost"}
      </Typography>
      <Typography
        id="connection-lost-description"
        sx={{ fontSize: 16, lineHeight: 1.5, color: leadColor, maxWidth: 340 }}
      >
        {lead}
      </Typography>

      <Box
        sx={{
          mt: 2.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1,
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 600,
          color: pill.text,
          backgroundColor: pill.bg,
          border: `1px solid ${pill.border}`,
        }}
      >
        {pillIcon}
        <span>{pillLabel}</span>
        <Box component="span" aria-hidden sx={{ opacity: 0.45 }}>·</Box>
        <Box
          component="span"
          title="Time without a connection"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {formatDuration(seconds)}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
          textAlign: "left",
          color: bodyColor,
          borderTop: `1px solid ${isDark ? colors.primary[300] : colors.grey[200]}`,
        }}
      >
        <Hint icon={TaskAltRoundedIcon} iconColor={palette.hintIcon}>
          {startup
            ? "You're still signed in. BatchMind opens by itself as soon as the server answers."
            : "No need to refresh or sign in again: you continue right where you left off."}
        </Hint>
        <Hint icon={SupportAgentRoundedIcon} iconColor={palette.hintIcon}>
          Still offline after a few minutes? Contact your maintenance team or
          system administrator.
        </Hint>
      </Box>

      {since && (
        <Typography sx={{ mt: 2.5, fontSize: 13, color: bodyColor }}>
          Contact lost at {formatClock(since)}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Full-page connection-lost screen for start-up: a stored session exists but
 * the server did not answer the token check (AuthContext keeps retrying).
 * @param {Object} props
 * @param {number} props.since - Epoch ms of the first failed check.
 */
export function ConnectionLostPage({ since }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="main"
      sx={{
        height: "100vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        p: 2.5,
        backgroundColor: "background.default",
      }}
    >
      <img
        alt="AG Automation logo"
        src={isDark ? "/assets/logo_white_large.png" : "/assets/logo_black_large.png"}
        style={{ width: "120px", objectFit: "contain" }}
      />
      <ConnectionLostCard since={since} startup />
    </Box>
  );
}

/**
 * App-wide overlay for a lost server connection, driven by the shared
 * machine-state stream. Mount once inside MachineStateProvider. Everything
 * behind it shows stale data, so it takes the pointer and keyboard focus and
 * cannot be dismissed.
 * See docs/design-decisions.md#ui-connection-lost-overlay
 */
export function ConnectionLostOverlay() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduceMotion = useMediaQuery(REDUCED_MOTION);
  const { isConnected } = useMachineState();
  const [phase, setPhase] = useState("hidden"); // hidden | lost | restored
  const [lostSince, setLostSince] = useState(null);

  useEffect(() => {
    if (isConnected === false) {
      if (phase === "restored") setPhase("lost");
      if (phase !== "hidden") return undefined;
      const droppedAt = Date.now();
      const timer = setTimeout(() => {
        setLostSince(droppedAt);
        setPhase("lost");
      }, GRACE_MS);
      return () => clearTimeout(timer);
    }
    if (isConnected === true && phase === "lost") {
      setPhase("restored");
      return undefined;
    }
    if (phase === "restored") {
      const timer = setTimeout(() => setPhase("hidden"), RESTORED_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isConnected, phase]);

  const open = phase !== "hidden";
  const fadeMs = reduceMotion ? 0 : 250;

  return (
    <Modal
      open={open}
      disableEscapeKeyDown
      closeAfterTransition
      // Above page dialogs, snackbars and tooltips: nothing behind it is live.
      sx={{ zIndex: (t) => t.zIndex.tooltip + 1 }}
      slotProps={{
        backdrop: {
          transitionDuration: fadeMs,
          sx: {
            backgroundColor: alpha(isDark ? "#0b0b0b" : "#f6f6f6", isDark ? 0.6 : 0.55),
            backdropFilter: "blur(6px) saturate(0.4)",
            WebkitBackdropFilter: "blur(6px) saturate(0.4)",
          },
        },
      }}
    >
      <Fade in={open} timeout={fadeMs}>
        <Box
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="connection-lost-title"
          aria-describedby="connection-lost-description"
          tabIndex={-1}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            outline: "none",
          }}
        >
          {/* Fading out shows the restored state, never "lost" again. */}
          <ConnectionLostCard since={lostSince} restored={phase !== "lost"} />
        </Box>
      </Fade>
    </Modal>
  );
}
